import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { addDemoVideo } from "redux/features/Admin/videos/videoSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import DashboardMenu from "../../DashboardPage/DashboardMenu";
import { FiVideo, FiUpload, FiAlertCircle } from "react-icons/fi";
import { z } from "zod";

export interface AddDemoVideoHomePageProps {
  className?: string;
}

const MAX_FILE_SIZE = 1024 * 1024 * 2000;
const ACCEPTED_VIDEO_MIME_TYPES = ["video/mp4", "video/mpeg", "video/webm", "video/mov"];
const formSchema = z.object({
  title: z.string().min(1, { message: "Le titre est requis." }),
  url: z
    .instanceof(File, { message: "La vidéo est requise." })
    .refine((file) => file?.size <= MAX_FILE_SIZE, `La taille maximale de la vidéo est de 2000 Mo.`)
    .refine(
      (file) => ACCEPTED_VIDEO_MIME_TYPES.includes(file?.type),
      "La vidéo doit être au format MP4, MPEG, WEBM ou MOV."
    ),
});

const AddDemoVideoHomePage: FC<AddDemoVideoHomePageProps> = ({ className = "" }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
    mode: "onSubmit",
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const videoState = useAppSelector((state) => state.videoHome);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);

  const watchedFile = watch("url");
  useEffect(() => {
    if (watchedFile instanceof File) {
      const url = URL.createObjectURL(watchedFile);
      setVideoPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setVideoPreview(null);
    }
  }, [watchedFile]);

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    setIsLoading(true);
    await dispatch(addDemoVideo(data)).then(() => {
      setIsLoading(false);
      navigate("/admin/dashboard/settings/home-demo-video");
    });
  };

  useEffect(() => {
    switch (videoState.loading) {
      case "pending":
        setIsLoading(true);
        break;
      case "succeeded":
      case "failed":
        setIsLoading(false);
        break;
    }
  }, [videoState.loading]);

  return (
    <div
      className={`nc-AccountPage ${className} bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-900 dark:to-neutral-800 min-h-screen`}
      data-nc-id="AccountPage"
    >
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-12 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl p-8 mx-auto bg-white shadow-lg dark:bg-neutral-800 rounded-2xl"
          >
            <div className="flex items-center mb-8 space-x-3">
              <FiVideo className="text-3xl text-primary-600" />
              <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white">Ajouter une Vidéo</h2>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Title Input */}
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Titre de la Vidéo
                </label>
                <input
                  {...register("title")}
                  type="text"
                  className="w-full px-4 py-2 text-gray-900 transition-colors bg-white border border-gray-300 rounded-lg dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-100 focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="Entrez le titre de la vidéo"
                />
                {errors.title?.message && (
                  <p className="flex items-center mt-1 text-xs text-red-500">
                    <FiAlertCircle className="mr-1" /> {errors.title.message}
                  </p>
                )}
              </div>

              {/* Video File Input */}
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fichier Vidéo
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="videoFile"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setValue("url", file);
                        trigger("url");
                      }
                    }}
                    accept="video/mp4,video/mpeg,video/webm,video/mov"
                  />
                  <label
                    htmlFor="videoFile"
                    className="flex items-center justify-center w-full px-4 py-3 text-gray-600 transition-colors border border-gray-300 border-dashed rounded-lg cursor-pointer dark:border-neutral-600 bg-gray-50 dark:bg-neutral-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-600"
                  >
                    <FiUpload className="mr-2 text-xl" />
                    {watchedFile ? watchedFile.name : "Choisir un fichier vidéo"}
                  </label>
                </div>
                {errors.url?.message && (
                  <p className="flex items-center mt-1 text-xs text-red-500">
                    <FiAlertCircle className="mr-1" /> {errors.url.message}
                  </p>
                )}
              </div>

              {/* Video Preview */}
              {videoPreview && (
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Aperçu de la Vidéo
                  </label>
                  <video
                    className="w-full rounded-lg"
                    src={videoPreview}
                    controls
                    onError={() => setVideoPreview(null)}
                  />
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-center pt-4">
                <ButtonPrimary
                  type="submit"
                  loading={isLoading}
                  className="w-full px-8 transition-transform duration-300 transform sm:w-auto hover:scale-105 btn-primary-gradient"
                >
                  Ajouter
                </ButtonPrimary>
              </div>
            </form>
          </motion.div>
        </div>
      </DashboardMenu>
    </div>
  );
};

export default AddDemoVideoHomePage;
